import type { ExerciseAnswerLetter, ExerciseLong, ExerciseMcq, ExerciseShort, TopicExercises, VerifiedExerciseMcq, VerifiedMcqUnit } from "~/features/computer/data/exercises/exercise.types";

const answerOrder = ["a", "b", "c", "d"] as const;

export function letterToIndex(letter: ExerciseAnswerLetter) {
  return answerOrder.indexOf(letter);
}

export function indexToLetter(index: number): ExerciseAnswerLetter {
  return answerOrder[index] ?? "a";
}

function toCoreMcq(question: VerifiedExerciseMcq): ExerciseMcq {
  const { correct, ...rest } = question;
  return rest;
}

export function defineVerifiedMcqUnit(meta: VerifiedMcqUnit["meta"], core: VerifiedExerciseMcq[], extra: ExerciseMcq[] = []): VerifiedMcqUnit {
  return { meta, core, extra };
}

export function toTopicExercises(unit: VerifiedMcqUnit, short: ExerciseShort[], long: ExerciseLong[]): TopicExercises {
  const core = unit.core.map(toCoreMcq);

  return {
    meta: unit.meta,
    core,
    extra: unit.extra,
    mcqs: core,
    practiceMcqs: [...core, ...unit.extra],
    short,
    long,
  };
}

export function validateUnit(unit: VerifiedMcqUnit, key: ExerciseAnswerLetter[]) {
  const errors: string[] = [];
  const wrongAnswers: string[] = [];
  const duplicateIds = unit.core
    .map((question) => question.id)
    .filter((id, index, all) => all.indexOf(id) !== index);

  if (unit.core.length !== unit.meta.totalMcqs || unit.core.length !== key.length) {
    errors.push("Count mismatch");
  }

  if (duplicateIds.length) {
    errors.push(`Duplicate IDs: ${duplicateIds.join(", ")}`);
  }

  const missingQuestions: string[] = [];

  unit.core.forEach((question, index) => {
    const expectedId = `u${unit.meta.unit.replace("unit-", "")}-q${index + 1}`;

    if (question.id !== expectedId) {
      missingQuestions.push(expectedId);
      errors.push(`Sequential ID mismatch at ${question.id}`);
    }

    if (question.correct !== key[index]) {
      wrongAnswers.push(question.id);
      errors.push(`Wrong answer at ${question.id}`);
    }
  });

  return {
    valid: errors.length === 0,
    errors,
    countMatches: unit.core.length === unit.meta.totalMcqs && unit.core.length === key.length,
    duplicateIds,
    missingQuestions,
    wrongAnswers,
  };
}
